import { Container, Box, Button, TextField, Typography, CircularProgress } from "@mui/material"
import { FaPlus } from "react-icons/fa"
import { useState } from "react"
import DialogNewProductBase from "./DialogProductBase"
import { batchService, BaseProduct, Batch } from "services/batchService"
import { ReturnArea } from "../Markets/ReturnArea"
import { Content, FilterContainer, TableContainer as StyledTableContainer } from "./styles"
import { Add as AddIcon } from "@mui/icons-material"
import { toast } from "react-toastify"
import { DialogUpload } from "./DialogUpload"
import DialogCreateBatch from "./DialogCreateBatch"
import DialogViewBatch from "./DialogViewBatch"
import { useTableParams } from "hooks/useTableParams"
import { Table as SharedTable } from "shared"

const Batches = () => {
  const [openCreateDialog, setOpenCreateDialog] = useState(false)
  const [openDialogProdutoBase, setOpenDialogProdutoBase] = useState(false)
  const [openViewBatchesPopup, setOpenViewBatchesPopup] = useState(false)
  const [selectedProduct, setSelectedProduct] = useState<BaseProduct | undefined>()
  const [batchForm, setBatchForm] = useState({
    batchId: 0,
    scheduledDate: "",
  })
  const [scheduledDate, setScheduledDate] = useState<string>(getInitialDeliveryDate())
  const [search, setSearch] = useState<string>("")
  const [openUploadDialog, setOpenUploadDialog] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isCreatingProduct, setIsCreatingProduct] = useState(false)

  const { count, data, params, refresh, onChange, status } = useTableParams({
    service: (params: any) => batchService.getAllBaseProducts(params),
  })

  function getInitialDeliveryDate() {
    const now = new Date()
    const hours = now.getHours()

    if (hours >= 7) {
      now.setDate(now.getDate() + 1)
    }

    const localDate = new Date(now.getTime() - now.getTimezoneOffset() * 60000)
      .toISOString()
      .split("T")[0]

    return localDate
  }

  const isPastInitialDate = (date: string) => {
    const initialDate = getInitialDeliveryDate()
    return new Date(date) < new Date(initialDate)
  }

  const handleSearchByDate = (date: any) => {
    setScheduledDate(date)
    onChange({ ...params, scheduledDate: date })
  }

  const handleSearch = (search: any) => {
    setSearch(search)
    onChange({ ...params, search })
  }

  const handleAddBatch = (product: BaseProduct) => {
    setSelectedProduct(product)
    setBatchForm({
      batchId: 0,
      scheduledDate: "",
    })
    setOpenCreateDialog(true)
  }

  const handleRequestCreateBatch = async () => {
    setIsSaving(true)
    try {
      await batchService.create({
        batchId: batchForm.batchId,
        productBaseId: selectedProduct?.productBaseId || 0,
        scheduledDate: scheduledDate,
      })
      toast.success("Lote criado com sucesso!")
      setOpenCreateDialog(false)
      setSelectedProduct(undefined)
      setBatchForm({ batchId: 0, scheduledDate: "" })
      refresh()
    } catch (error: any) {
      toast.error(error?.message ?? "Erro ao criar lote. Tente novamente.")
    } finally {
      setIsSaving(false)
    }
  }

  const handleSubmitProdutoBase = async (data: { productBaseId: number; batches: string[] }) => {
    try {
      setIsCreatingProduct(true)
      const validBatches = data.batches.filter(batchId => batchId.trim() !== "")

      if (validBatches.length === 0) {
        toast.error("Adicione pelo menos um lote válido")
        return
      }

      for (const batchId of validBatches) {
        await batchService.create({
          batchId: Number(batchId),
          productBaseId: data.productBaseId,
          scheduledDate: scheduledDate,
        })
      }

      toast.success("Lote(s) criados com sucesso!")
      setOpenDialogProdutoBase(false)
      refresh()
    } catch (error: any) {
      toast.error(error?.message ?? "Erro ao criar lotes. Tente novamente.")
    } finally {
      setIsCreatingProduct(false)
    }
  }

  const handleOpenViewBatchesPopup = (data: BaseProduct) => {
    setSelectedProduct(data)
    setOpenViewBatchesPopup(true)
  }

  const handleCloseViewBatchesPopup = () => {
    setSelectedProduct(undefined)
    setOpenViewBatchesPopup(false)
    refresh()
  }

  return (
    <>
      <Container>
        <ReturnArea title="Lotes" />
        <Content>
          <FilterContainer>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                justifyContent: "space-between",
                alignItems: { xs: "stretch", sm: "center" },
                gap: 2,
                width: "100%",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  gap: 2,
                  width: "100%",
                  maxWidth: "600px",
                }}
              >
                <TextField
                  label="Data de entrega"
                  type="date"
                  value={scheduledDate}
                  onChange={e => handleSearchByDate(e.target.value)}
                  InputLabelProps={{
                    shrink: true,
                    style: { color: "#F8974C" },
                  }}
                  sx={{ width: { xs: "100%", sm: "150px" } }}
                />
                <TextField
                  label="Buscar lote ou produto"
                  value={search}
                  onChange={e => handleSearch(e.target.value)}
                  fullWidth
                  sx={{ width: { xs: "100%", sm: "250px" } }}
                  InputLabelProps={{
                    style: { color: "#F8974C" },
                  }}
                />
              </Box>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  gap: 2,
                  justifyContent: { xs: "center", sm: "flex-end" },
                  alignItems: "center",
                  width: { xs: "100%", sm: "auto" },
                }}
              >
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<AddIcon />}
                  onClick={() => setOpenUploadDialog(true)}
                  disabled={isPastInitialDate(scheduledDate)}
                  sx={{ width: { xs: "100%", sm: "auto" } }}
                >
                  Em massa
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<AddIcon />}
                  onClick={() => setOpenDialogProdutoBase(true)}
                  disabled={isPastInitialDate(scheduledDate)}
                  sx={{ width: { xs: "100%", sm: "auto" } }}
                >
                  Novo Produto
                </Button>
              </Box>
            </Box>
          </FilterContainer>

          <StyledTableContainer>
            {status === "pending" ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                <CircularProgress color="secondary" />
              </Box>
            ) : data.length === 0 ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                <Typography>Nenhum produto encontrado</Typography>
              </Box>
            ) : (
              <SharedTable<BaseProduct>
                columns={[
                  {
                    field: "productBaseName",
                    label: "Produto",
                  },
                  {
                    field: "Batchs",
                    label: "Lotes",
                    renderCell: (row: any) => row.data.Batchs.length,
                  },
                  {
                    field: "showBatches",
                    label: "",
                    renderCell: (row: any) => (
                      <Box display="flex" gap={1}>
                        <Button
                          variant="contained"
                          color="secondary"
                          size="medium"
                          onClick={() => handleOpenViewBatchesPopup(row.data)}
                        >
                          Ver Lotes
                        </Button>
                      </Box>
                    ),
                  },
                  {
                    field: "addBatch",
                    label: "",
                    renderCell: (row: any) => (
                      <Box display="flex" gap={1}>
                        <Button
                          variant="contained"
                          color="secondary"
                          size="medium"
                          startIcon={<FaPlus />}
                          onClick={() => handleAddBatch(row.data)}
                          disabled={isPastInitialDate(scheduledDate)}
                        >
                          Lote
                        </Button>
                      </Box>
                    ),
                  },
                ]}
                data={data}
                params={params}
                onChange={onChange}
                refresh={refresh}
                count={count}
                status={status}
                rowStyle={() => ({ cursor: "pointer" })}
              />
            )}
          </StyledTableContainer>
        </Content>
      </Container>
      <DialogNewProductBase
        open={openDialogProdutoBase}
        onClose={() => !isCreatingProduct && setOpenDialogProdutoBase(false)}
        onSubmit={handleSubmitProdutoBase}
        isLoading={isCreatingProduct}
        scheduledDate={scheduledDate}
      />
      <DialogCreateBatch
        openDialog={openCreateDialog}
        setOpenDialog={setOpenCreateDialog}
        handleSubmitBatch={handleRequestCreateBatch}
        isSaving={isSaving}
        batchForm={{ ...batchForm, scheduledDate }}
        setBatchForm={setBatchForm}
        scheduledDate={scheduledDate}
        selectedProduct={selectedProduct}
      />

      <DialogUpload isOpen={openUploadDialog} setIsOpen={setOpenUploadDialog} refresh={refresh} />
      <DialogViewBatch
        openDialog={openViewBatchesPopup}
        setOpenDialog={setOpenViewBatchesPopup}
        onClose={handleCloseViewBatchesPopup}
        data={selectedProduct}
        isDisabled={isPastInitialDate(scheduledDate)}
        selectedProduct={selectedProduct}
      />
    </>
  )
}

export default Batches
